import type {
  CarouselBrandItem,
  CarouselCategoryItem,
  CarouselItems,
  CarouselItemType,
  CarouselProductItem,
} from "./types";
import {
  isCatalogProduct,
  isCategoryData,
  isHomepagePopularCategoryData,
  isHomePageSingleBrand,
  isHomepageSingleCategoryProduct,
  isPopularCategoryType,
} from "src/utils/type-guards";

export const isIOSApp =
  typeof webkit !== "undefined" &&
  typeof webkit.messageHandlers?.track !== "undefined";

export const isAndroidApp = typeof segment_webview !== "undefined";

export const isNativeApp = isAndroidApp || isIOSApp;

export function requestSource(): {
  request_source: "appAndroid" | "appIos" | "webReact";
} {
  const searchParams = new URLSearchParams(location.search);
  const isFromAndroid = searchParams.get("app") === "android";
  let source: "appAndroid" | "appIos" | "webReact" = "webReact";

  if (isAndroidApp || isFromAndroid) {
    source = "appAndroid";
  }

  if (isIOSApp) {
    source = "appIos";
  }

  return { request_source: source };
}

export function buildProductUrl(slug: string): string {
  if (typeof window === "undefined" || !location) {
    return "";
  }

  return `${location.origin}/product/${slug}`;
}

const FLASH_SALE = "FLASH_SALE";

// Read through a widened type: these fields are non-optional in the generated
// schema types but absent at runtime when a query does not select them.
function currencyCode(item: {
  currency?: { code?: string };
}): string | undefined {
  return item.currency?.code;
}

function isFlashSale(item: object): boolean | undefined {
  const { brand_sale_type, brand } = item as {
    brand_sale_type?: string | null;
    brand?: { brand_sale_type?: string | null };
  };
  const saleType = brand_sale_type ?? brand?.brand_sale_type;

  return saleType ? saleType === FLASH_SALE : undefined;
}

export function mapCarouselItem(
  item: CarouselItemType,
  position: number
): CarouselItems | undefined {
  if (isHomepagePopularCategoryData(item) || isCategoryData(item)) {
    return {
      category_id: item.id,
      name: item.title,
      position,
      slug: item.slug,
    } satisfies CarouselCategoryItem;
  }

  if (isPopularCategoryType(item)) {
    return {
      category_id: item.id,
      name: item.name,
      position,
      slug: item.slug,
    } satisfies CarouselCategoryItem;
  }

  if (isHomepageSingleCategoryProduct(item)) {
    return {
      brand: item.brand.name,
      brand_id: item.brand.id,
      image_url: item.thumbnail,
      name: item.name,
      position,
      labels: item.labels?.map((label) => label.value),
      product_id: item.id,
      price: item.price_float,
      regular_price: item.regular_price_float,
      product_discount: item.product_discount as number,
      currency: currencyCode(item),
      flash_sale_is_on: isFlashSale(item),
      category: item.translated_category_names as string[],
      category_id: item.translated_category_ids as number[],
      product_shop_type: item.product_shop_type as string[],
      sizes_available: item.sizes_available as string,
      url: (item.url as string) || buildProductUrl(item.slug),
      product_kids_gender: item.product_kids_gender as string[],
      slug: item.slug,
      gate_level: item.gate?.level || undefined,
    } satisfies CarouselProductItem;
  }

  if (isCatalogProduct(item)) {
    return {
      brand: item.brand.name,
      brand_id: item.brand.id,
      image_url: item.thumbnail,
      name: item.name,
      position,
      labels: item.labels?.map((label) => label.value),
      product_id: item.id,
      price: item.price,
      regular_price: item.regular_price,
      product_discount: item.product_discount as number,
      currency: currencyCode(item),
      flash_sale_is_on: isFlashSale(item),
      category: item.translated_category_names as string[],
      category_id: item.translated_category_ids as number[],
      product_shop_type: item.product_shop_type as string[],
      sizes_available: item.available_sizes_label as string,
      url: (item.url as string) || buildProductUrl(item.slug),
      product_kids_gender: item.product_kids_gender as string[],
      slug: item.slug,
      gate_level: item.gate?.level || undefined,
    } satisfies CarouselProductItem;
  }

  if (isHomePageSingleBrand(item)) {
    return {
      brand_id: item.id,
      image_url: item.image,
      logo_url: item.logo,
      name: item.name,
      position,
      labels: item.labels?.map((label) => label.value),
      gate_level: item.gate?.level || undefined,
    } satisfies CarouselBrandItem;
  }

  return;
}

export function mapCarouselItems(items: CarouselItemType[]): CarouselItems[] {
  return items
    .map((item, idx) => mapCarouselItem(item, idx + 1))
    .filter((item): item is CarouselItems => item !== undefined);
}

// Maps against the original indexes so positions stay aligned with `items`
// even when an entry is dropped by mapCarouselItem.
export function mapVisibleCarouselItems(
  items: CarouselItemType[],
  visibleIndexes: number[]
): CarouselItems[] {
  return items
    .map((item, idx) =>
      visibleIndexes.includes(idx) ? mapCarouselItem(item, idx + 1) : undefined
    )
    .filter((item): item is CarouselItems => item !== undefined);
}
